/* import __COLOCATED_TEMPLATE__ from './expanded-parameter.hbs'; */
﻿import Component from '@ember/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import formulas from '../utils/formulas';
import $ from 'jquery';
import { eng3 } from '@ebiz/pretty-num';
import { tracked } from '@glimmer/tracking';
import Comparator from '@ebiz/ember-aditools-device-table/utils/comparator';
import PartialTextFilter from '@ebiz/ember-aditools-device-table/utils/partial-text-filter';
import EngineeringRangeValueFilter from '@ebiz/ember-aditools-device-table/utils/engineering-range-value-filter';
import MultivalueCheckboxFilter from '@ebiz/ember-aditools-device-table/utils/multivalue-checkbox-filter';

var validGainRangeString = function (part) {
  if ('fixed' === part.gainType.toLowerCase()) {
    return part.gainInputs[0].description;
  }

  if ((part.gainRangeMin || part.gainRangeMin === 0) && (part.gainRangeMax || part.gainRangeMax === 0)) {
    return part.gainRangeMin.toString() + ' to ' + part.gainRangeMax.toString();
  } else {
    return '';
  }
};

var constructRow = function (part, gain, g, f) {
  if (null == g) {
    return null;
  }

  return {
    id: part.id,
    description: part.description,
    iosMax: eng3(g.iosMax),
    isyMaxPerAmp: eng3(g.isyMaxPerAmp),
    validGains: validGainRangeString(part),
    vos: eng3(f.calVos(g, gain)),
    noiseDensity: eng3(f.calNoiseDensity(g, gain)),
    cmrr: eng3(f.calCMRR(g, gain, part.gainRangeMin)),
    bw: eng3(f.calBW(part, g, gain)),
    url: part.url,
    sortOrder: part.order,
  };
};

var _getFilteredAndSortedValues = function (data, field) {
  const fieldValues = [];
  data.forEach((item) => {
    fieldValues.push(item[field]);
  });
  const filteredAndSortedValues = fieldValues
    .filter((fieldValue, i) => {
      return fieldValues.lastIndexOf(fieldValue) === i;
    })
    .sort((a, b) => {
      return a < b ? -1 : 1;
    });
  return filteredAndSortedValues;
};

////////////////////////////////////////////////////////////////////////////////////
// expanded-parameter - Filter-able recommended parts list dialog box component   //
////////////////////////////////////////////////////////////////////////////////////

export default class ExpandedParameter extends Component {
  tagName = '';
  @service('aditools-analytics') analytics;
  @service intl;
  @service site;

  @tracked data = [];
  @tracked options = {
    asyncEditorLoading: true,
    showHeaderRow: true,
    resizable: true,
    forceFitColumns: false,
    multiSelect: false,
    headerRowHeight: 32,
    rowHeight: 25,
    autoHeight: false,
    enableColumnReorder: false,
    multiColumnSort: true,
    frozenColumn: 0,
    explicitInitialization: true,
  };
  @tracked columns = [];
  @tracked locale = this.site.AdiLocale;
  @tracked inAmpsSummaryRowText = this.intl.t('expandedParameterView.summaryText');

  ////////////////////////////////////////////////////////////////////////////////
  // User Functions                                                             //
  ////////////////////////////////////////////////////////////////////////////////

  constructColumns() {
    var adiBaseURL = this.adiBaseURL,
      self = this;

    let linkFormatter = function (row, cell, value, columnDef, dataContext) {
      return [
        '<span class="',
        value.toLowerCase(),
        '-part-number-cell part-number-cell">',
        value,
        '<a target="_blank"',
        'class="recommended-part-item"',
        'rel="noopener noreferrer"',
        'data-action="part"',
        'data-partid="',
        value,
        '"',
        ' href="',
        adiBaseURL,
        '/',
        dataContext.url,
        self.urlQuestion,
        '">',
        '<span class="link-icon"></span>',
        '</a>',
        '</span>',
      ].join('');
    };
    // If the column has a filter its width should be at least 110
    this.columns = [
      {
        name: this.intl.t('expandedParameterView.name'),
        field: 'id',
        id: 'name',
        width: 115,
        sortable: true,
        formatter: linkFormatter,
        filter: new PartialTextFilter('', 'part'),
      },
      {
        id: 'validGains',
        name: this.intl.t('expandedParameterView.validGains'),
        field: 'validGains',
        width: 110,
        filter: new MultivalueCheckboxFilter(this.buildFilterOptions(this.data, 'validGains')),
      },
      {
        id: 'cmrr',
        name: this.intl.t('expandedParameterView.cmrr'),
        field: 'cmrr',
        width: 120,
        sortable: true,
        comparator: Comparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'vos',
        name: this.intl.t('expandedParameterView.vos'),
        field: 'vos',
        width: 120,
        sortable: true,
        comparator: Comparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'iosMax',
        name: this.intl.t('expandedParameterView.iosMax'),
        field: 'iosMax',
        width: 120,
        sortable: true,
        comparator: Comparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'noiseDensity',
        name: this.intl.t('expandedParameterView.noiseDensity'),
        field: 'noiseDensity',
        width: 165,
        sortable: true,
        comparator: Comparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'bw',
        name: this.intl.t('expandedParameterView.bw'),
        field: 'bw',
        width: 120,
        sortable: true,
        comparator: Comparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'isyMaxPerAmp',
        name: this.intl.t('expandedParameterView.isyMaxPerAmp'),
        field: 'isyMaxPerAmp',
        width: this.locale === 'jp' ? 190 : this.locale === 'cn' ? 170 : 160,
        sortable: true,
        comparator: Comparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'description',
        name: this.intl.t('expandedParameterView.description'),
        field: 'description',
        width: 420,
        filter: new PartialTextFilter(''),
      },
    ];
  }

  populateRows() {
    // Clear out the existing data if there is any.
    this.data = [];
    // Populate the rows.
    var validParts = this.recommendedParts;
    if (validParts == null) {
      return;
    }

    for (var i = 0; i < validParts.length; i++) {
      var row = constructRow(
        validParts[i],
        Number(this.selectedGainInput.description),
        this.gainDep[validParts[i].id],
        formulas,
      );
      if (null != row) {
        this.data.push(row);
      }
    }
  }

  sendAnalytics(act, partId) {
    switch (act) {
      case 'recommendedList':
        this.analytics.setWebEvent(this.toolConfigData.toolName, 'openParametricTable', this.recommendedInAmpsUrl);
        break;
      case 'recommended':
        this.analytics.setWebEvent(this.toolConfigData.toolName, 'openParametricTable', '');
        break;
      case 'part':
        if (partId) this.analytics.setWebEvent(this.toolConfigData.toolName, 'productView', partId);
        break;
    }
  }

  buildFilterOptions(data, field) {
    const filterValues = _getFilteredAndSortedValues(data, field)
      .map((item) => {
        return { value: item, display: item.toString() };
      })
      .filter((item) => {
        return item.value && item.display.trim() !== '';
      });
    return filterValues;
  }
  ////////////////////////////////////////////////////////////////////////////////
  // Component Actions                                                          //
  ////////////////////////////////////////////////////////////////////////////////
  @action insertingGrid() {
    var self = this;
    this.populateRows();
    this.constructColumns();
    $('a.recommended-part-item, #expandedParametricGrid').click(function (evt) {
      var act = evt.target.getAttribute('data-action') || '';
      var partId = evt.target.getAttribute('data-partid');
      self.sendAnalytics(act, partId);
    });

    this.analytics.setWebEvent(this.toolConfigData.toolName, 'expandedTableViewed', '');
  }

  @action linkClicked(evt) {
    var act = evt.target.getAttribute('data-action') || '';
    this.sendAnalytics(act, '');
  }

  @action insertingHover() {
    $(function () {
      $('#expanded-parameter-links .link-with-img').hover(
        function () {
          var e = $(this).find('>img:first-of-type');
          e.data('originalSrc', e.attr('src'));
          e.attr('src', e.attr('data-rollover'));
        },
        function () {
          var e = $(this).find('>img:first-of-type');
          e.attr('src', e.data('originalSrc'));
        },
      );
    });
  }

  @action resetExpandedFilters() {
    this.insertingGrid();
  }
}
